import { useEffect, useState, useSyncExternalStore } from "react";
import { api, fetchBlob } from "./api.ts";

/* Profile photos, shared by every avatar on screen. The list of who has one
 * is fetched once (and again after a change); each picture is fetched once,
 * with the app's own credentials, and kept as a local object URL. */

let versions: Record<string, number> = {};
const listeners = new Set<() => void>();
const urls = new Map<string, Promise<string>>();

export function refreshPhotos(): Promise<void> {
  return api.photos().then((r) => { versions = r.photos; listeners.forEach((l) => l()); }).catch(() => {});
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** The picture for a clinician, or null while loading or when they have none. */
export function usePhoto(id: string | undefined): string | null {
  const v = useSyncExternalStore(subscribe, () => (id ? versions[id] : undefined));
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!id || !v) { setUrl(null); return; }
    const key = `${id}:${v}`;
    let p = urls.get(key);
    if (!p) {
      p = fetchBlob(`/api/clinicians/${id}/photo?v=${v}`).then((b) => URL.createObjectURL(b));
      urls.set(key, p);
      p.catch(() => urls.delete(key));
    }
    let live = true;
    p.then((u) => live && setUrl(u)).catch(() => live && setUrl(null));
    return () => { live = false; };
  }, [id, v]);
  return url;
}

/** Crop a chosen picture to a centred square and shrink it to 256 px JPEG. */
export async function squarePhoto(file: File, size = 256): Promise<string> {
  const img = await createImageBitmap(file);
  const side = Math.min(img.width, img.height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.86);
}
